import type { AssigneeOption } from '../../lib/vm/useDocumentEditorVM';

/** Property kinds rendered in the editor property bar. */
export type PropertyBarDefKind =
  | 'user'
  | 'single_select'
  | 'multi_select'
  | 'text'
  | 'number'
  | 'url'
  | 'date';

/** Minimal def shape for display resolution — pills only need kind + label. */
export interface PropertyBarDef {
  kind: PropertyBarDefKind | string;
  label: string;
}

export interface PropertyBarSelectOption {
  id: string;
  label: string;
}

export interface PropertyBarDisplayContext {
  userOptions?: AssigneeOption[];
  selectOptions?: PropertyBarSelectOption[];
}

const EMPTY_PLACEHOLDER = '—';
const TYPED_KINDS = new Set<PropertyBarDefKind>([
  'text',
  'number',
  'url',
  'date'
]);

/** True when the pill should use the muted empty styling. */
export function isPropertyPillMuted(displayValue: string | null): boolean {
  return displayValue == null || displayValue === '';
}

/** Formats a pill label as `"Label: value"` with an em dash when unset. */
export function formatPropertyPillLabel(
  label: string,
  displayValue: string | null
): string {
  const value =
    displayValue == null || displayValue === ''
      ? EMPTY_PLACEHOLDER
      : displayValue;
  return `${label}: ${value}`;
}

function readUserId(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    const userId = (value as { user_id?: unknown }).user_id;
    if (typeof userId === 'string' && userId.length > 0) {
      return userId;
    }
  }
  return null;
}

function readOptionId(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    const optId = (value as { option_id?: unknown }).option_id;
    if (typeof optId === 'string' && optId.length > 0) {
      return optId;
    }
  }
  return null;
}

function readOptionIds(value: unknown): string[] {
  if (value == null) return [];
  if (typeof value === 'object' && !Array.isArray(value)) {
    const ids = (value as { option_ids?: unknown }).option_ids;
    if (Array.isArray(ids)) {
      return ids.filter(
        (id): id is string => typeof id === 'string' && id.length > 0
      );
    }
  }
  return [];
}

function readTypedScalar(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    return value.trim() === '' ? null : value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function formatDateDisplay(iso: string): string {
  const day = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return iso;
  const parsed = new Date(`${day}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/** Resolves a human-readable display string from a stored property value. */
export function resolveScalarDisplay(
  def: PropertyBarDef,
  value: unknown,
  context: PropertyBarDisplayContext = {}
): string | null {
  if (def.kind === 'user') {
    const userId = readUserId(value);
    if (!userId) return null;
    const match = (context.userOptions ?? []).find((u) => u.user_id === userId);
    return match?.display_name ?? null;
  }

  if (def.kind === 'single_select') {
    const optionId = readOptionId(value);
    if (!optionId) return null;
    const match = (context.selectOptions ?? []).find((o) => o.id === optionId);
    return match?.label ?? '(missing)';
  }

  if (def.kind === 'multi_select') {
    const optionIds = readOptionIds(value);
    if (optionIds.length === 0) return null;
    const byId = new Map((context.selectOptions ?? []).map((o) => [o.id, o]));
    const labels = optionIds.map((id) => byId.get(id)?.label ?? '(missing)');
    return labels.join(', ');
  }

  if (TYPED_KINDS.has(def.kind as PropertyBarDefKind)) {
    const scalar = readTypedScalar(value);
    if (scalar == null) return null;
    if (def.kind === 'date') return formatDateDisplay(scalar);
    return scalar;
  }

  return null;
}
